// ─────────────────────────────────────────────────────────────────────────────
// Voice Mode 2.0 — Phase P0 "Visible Spine": step executors.
//
// Spec: docs/plans/voice-mode-ui-control-protocol_v1_2026-09-19.md §4–§5
// The executor drives the REAL UI exactly like a user: navigate routes, click
// the real buttons (native click → React's delegated handlers → identical
// validations), highlight via the overlay, scroll targets into view.  P0 ships
// navigate/click/highlight/scrollTo; fill/select/focus/openPanel/toast come
// with the P2 plan/execute split.
// ─────────────────────────────────────────────────────────────────────────────

import { UiTargetMissingError, waitForAgentId } from './registry';
import type { UiStepKind } from './registry';
import { isHarnessMode, recordTrace } from './harness';
import { captureViewport, uploadScreenshot } from './screenshot';

/** WCP §2.3 choreography constants (owner contract 2026-09-20):
 *  ring → action ≈ 220 ms; visible typing 28 ms/char; step gap 80–160 ms. */
export const WCP_CHOREOGRAPHY = {
  RING_TO_ACTION_MS: 220,
  TYPE_CHAR_DELAY_MS: 28,
  RING_TTL_MS: 900,
  PRESS_PULSE_MS: 500,
} as const;

export interface UiStep {
  stepId: string;
  kind: UiStepKind;
  /** Target for click/highlight/scrollTo. */
  agentId?: string;
  /** In-app route for navigate. */
  to?: string;
  /** Value for type/select steps. */
  value?: unknown;
  /** Type steps: visible typing animation on/off (default on). */
  withTyping?: boolean;
  /** Highlight lifetime for the overlay ring. */
  ttlMs?: number;
  /** Request steps (2026-09-21): what the agent asked the browser to do.
   *  Currently only 'screenshot' — the agent looking at the screen. */
  request?: string;
  /** One short line describing why (used as the screenshot reason). */
  intent?: string;
}

export type UiStepStatus = 'ok' | 'target_missing' | 'timeout' | 'error' | 'skipped';

export interface UiStepResult {
  status: UiStepStatus;
  detail?: string;
  /** Read-back evidence sent with the ack (never optimistic — spec §5.2). */
  evidence?: Record<string, unknown>;
}

export interface UiExecutorContext {
  /** SPA router navigation (react-router's navigate; fallback: history+popstate). */
  navigate?: (to: string) => void;
  /** Called by the highlight executor so the overlay can draw the ring. */
  onHighlight?: (agentId: string, ttlMs: number) => void;
  /** Visible settle gap between steps (default 140 ms, WCP §2.3: 80–160 ms). */
  settleMs?: number;
  /** Budget for a target to appear (default 3 s, spec §7). */
  targetTimeoutMs?: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** In-app route guard: only same-origin SPA paths, never javascript:/external. */
function assertInAppRoute(to: string): void {
  if (!to.startsWith('/') || to.startsWith('//')) {
    throw new Error(`navigate target must be an in-app route, got ${to}`);
  }
}

/** Fallback SPA navigation when no router callback is injected:
 *  pushState + popstate keeps react-router in sync without a reload. */
function navigateFallback(to: string): void {
  window.history.pushState({}, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function scrollIntoViewCenter(el: HTMLElement): void {
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch {
    try {
      el.scrollIntoView(); // older engines
    } catch {
      /* jsdom: scrollIntoView is not implemented — ignore */
    }
  }
}

/** Execute ONE plan step and return a result for the ack. */
export async function executeStep(
  step: UiStep,
  ctx: UiExecutorContext = {},
): Promise<UiStepResult> {
  const startedAt = Date.now();
  const tMs = () => Date.now() - startedAt;

  try {
    switch (step.kind) {
      case 'today': {
        // "open today on the calendar" (owner 2026-09-21). The day-cell controls
        // carry date-derived ids that change every day, so the agent could not
        // reliably address "today". This asks the Calendar page to jump to today
        // and open the current day, via one stable labelled control.
        const el = await waitForAgentId('calendar.today', ctx.targetTimeoutMs).catch(() => null);
        if (el) {
          ctx.onHighlight?.('calendar.today', WCP_CHOREOGRAPHY.RING_TTL_MS);
          await sleep(WCP_CHOREOGRAPHY.RING_TO_ACTION_MS);
          el.click();
        }
        const todayKey = new Date().toLocaleDateString('en-CA');
        const cell = document.querySelector<HTMLElement>(`[data-agent-id="calendar.day.${todayKey}"]`)
          || document.querySelector<HTMLElement>('[data-agent-id^="calendar.day."]');
        if (cell) {
          ctx.onHighlight?.(cell.getAttribute('data-agent-id') || '', WCP_CHOREOGRAPHY.RING_TTL_MS);
          await sleep(WCP_CHOREOGRAPHY.RING_TO_ACTION_MS);
          cell.click();
        }
        recordTrace({ type: 'action', stepId: step.stepId, agentId: 'calendar.today', at: Date.now(), data: { kind: 'today' } });
        await sleep(ctx.settleMs ?? 140);
        if (!el && !cell) return { status: 'target_missing', detail: 'calendar today control not found' };
        return { status: 'ok', evidence: { today: todayKey, clicked: Boolean(cell) } };
      }

      case 'navigate': {
        // Accept both protocol field names: `to` (WCP spec / demo shim) and
        // `agentId` (what ui_plan.build_plan emits for the ui_act tool).
        const to = step.to ?? step.agentId ?? '';
        assertInAppRoute(to);
        if (ctx.navigate) ctx.navigate(to);
        else navigateFallback(to);
        // Harness mode: give lazy pages + data fetches time to render (the
        // batch asserts the CONTROLS after navigation, not navigation itself).
        await sleep(isHarnessMode() ? 950 : (ctx.settleMs ?? 140));
        recordTrace({ type: 'navigate', stepId: step.stepId, at: Date.now(), data: { to, finalPath: window.location.pathname } });
        return {
          status: 'ok',
          evidence: { to, finalPath: window.location.pathname },
        };
      }

      case 'click': {
        if (!step.agentId) return { status: 'error', detail: 'click step missing agentId' };
        const el = await waitForAgentId(step.agentId, ctx.targetTimeoutMs);
        scrollIntoViewCenter(el); // focus cue: visible before the press
        // The user must SEE what is about to be touched: ring the target first,
        // then press it (owner report 2026-09-20: "there is no animation").
        const ringAt = Date.now();
        ctx.onHighlight?.(step.agentId, WCP_CHOREOGRAPHY.RING_TTL_MS);
        recordTrace({ type: 'ring', stepId: step.stepId, agentId: step.agentId, at: ringAt });
        await sleep(WCP_CHOREOGRAPHY.RING_TO_ACTION_MS); // WCP §2.3: focus cue → action
        el.setAttribute('data-agent-press', 'true');
        recordTrace({ type: 'press', stepId: step.stepId, agentId: step.agentId, at: Date.now() });
        window.setTimeout(() => el.removeAttribute('data-agent-press'), WCP_CHOREOGRAPHY.PRESS_PULSE_MS);
        el.click(); // native click → real React handlers, same as a user
        recordTrace({ type: 'action', stepId: step.stepId, agentId: step.agentId, at: Date.now(), data: { kind: 'click', ringToActionMs: Date.now() - ringAt } });
        await sleep(ctx.settleMs ?? 140);
        return {
          status: 'ok',
          evidence: {
            agentId: step.agentId,
            tag: el.tagName.toLowerCase(),
            text: (el.textContent || '').trim().slice(0, 80) || null,
          },
        };
      }

      case 'type': {
        if (!step.agentId) return { status: 'error', detail: 'type step missing agentId' };
        const el = await waitForAgentId(step.agentId, ctx.targetTimeoutMs);
        if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) {
          return { status: 'error', detail: `type target is not a text field: ${el.tagName.toLowerCase()}` };
        }
        scrollIntoViewCenter(el);
        const ringAt = Date.now();
        ctx.onHighlight?.(step.agentId, WCP_CHOREOGRAPHY.RING_TTL_MS);
        recordTrace({ type: 'ring', stepId: step.stepId, agentId: step.agentId, at: ringAt });
        await sleep(WCP_CHOREOGRAPHY.RING_TO_ACTION_MS);
        const value = String(step.value ?? '');
        const proto = el instanceof HTMLTextAreaElement
          ? HTMLTextAreaElement.prototype
          : HTMLInputElement.prototype;
        const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
        // Typing visibly: set the value through the native setter and fire the
        // events React listens for, so the field behaves as if a user typed.
        const chars = value.split('');
        const sampleLens: number[] = [];
        const sampleAt: number[] = [];
        const samplePoints = new Set([0, Math.floor(chars.length * 0.25), Math.floor(chars.length * 0.5), Math.floor(chars.length * 0.75), Math.max(0, chars.length - 1)]);
        el.focus();
        for (let i = 0; i < chars.length; i += 1) {
          const next = chars.slice(0, i + 1).join('');
          if (setter) setter.call(el, next); else el.value = next;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          if (samplePoints.has(i)) { sampleLens.push(i + 1); sampleAt.push(Date.now() - ringAt); }
          await sleep(step.withTyping === false ? 0 : WCP_CHOREOGRAPHY.TYPE_CHAR_DELAY_MS);
        }
        el.dispatchEvent(new Event('change', { bubbles: true }));
        recordTrace({
          type: 'type', stepId: step.stepId, agentId: step.agentId, at: Date.now(),
          data: { chars: chars.length, charDelayMs: WCP_CHOREOGRAPHY.TYPE_CHAR_DELAY_MS, sampleLens, sampleAt, inputEvents: chars.length, value: value.slice(0, 200) },
        });
        await sleep(ctx.settleMs ?? 140);
        return { status: 'ok', evidence: { agentId: step.agentId, value } };
      }

      case 'select': {
        if (!step.agentId) return { status: 'error', detail: 'select step missing agentId' };
        const el = await waitForAgentId(step.agentId, ctx.targetTimeoutMs);
        scrollIntoViewCenter(el);
        const ringAt = Date.now();
        ctx.onHighlight?.(step.agentId, WCP_CHOREOGRAPHY.RING_TTL_MS);
        recordTrace({ type: 'ring', stepId: step.stepId, agentId: step.agentId, at: ringAt });
        await sleep(WCP_CHOREOGRAPHY.RING_TO_ACTION_MS);
        const value = String(step.value ?? '');
        if (el instanceof HTMLSelectElement) {
          (el as HTMLSelectElement).value = value;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          el.click(); // radios, checkboxes, custom toggles
        }
        recordTrace({ type: 'action', stepId: step.stepId, agentId: step.agentId, at: Date.now(), data: { kind: 'select', value, ringToActionMs: Date.now() - ringAt } });
        await sleep(ctx.settleMs ?? 140);
        return { status: 'ok', evidence: { agentId: step.agentId, value } };
      }

      case 'highlight': {
        if (!step.agentId) return { status: 'error', detail: 'highlight step missing agentId' };
        const el = await waitForAgentId(step.agentId, ctx.targetTimeoutMs);
        scrollIntoViewCenter(el);
        ctx.onHighlight?.(step.agentId, step.ttlMs ?? 4000);
        recordTrace({ type: 'highlight', stepId: step.stepId, agentId: step.agentId, at: Date.now(), data: { ttlMs: step.ttlMs ?? 4000 } });
        return { status: 'ok', evidence: { agentId: step.agentId } };
      }

      case 'scrollTo': {
        if (!step.agentId) return { status: 'error', detail: 'scrollTo step missing agentId' };
        const el = await waitForAgentId(step.agentId, ctx.targetTimeoutMs);
        scrollIntoViewCenter(el);
        recordTrace({ type: 'scrollTo', stepId: step.stepId, agentId: step.agentId, at: Date.now() });
        return { status: 'ok', evidence: { agentId: step.agentId } };
      }

      case 'request': {
        // Owner directive (2026-09-21): the agent may ask to LOOK at the screen
        // when the labelled control list leaves it unsure. Capture the viewport,
        // upload it, and ack with the stored path so the agent can view it.
        const request = String((step as { request?: string }).request || 'screenshot');
        if (request !== 'screenshot') {
          return { status: 'skipped', detail: `unknown request: ${request}` };
        }
        const shot = await captureViewport();
        if (!shot) return { status: 'error', detail: 'screen capture unavailable' };
        const stored = await uploadScreenshot(shot, {
          route: window.location.pathname,
          reason: step.intent,
        });
        recordTrace({ type: 'request', stepId: step.stepId, agentId: 'screenshot', at: Date.now(),
          data: { bytes: stored?.bytes ?? 0, route: window.location.pathname } });
        return {
          status: 'ok',
          evidence: {
            path: stored?.path,
            route: window.location.pathname,
            width: shot.width,
            height: shot.height,
          },
        };
      }

      default:
        return { status: 'skipped', detail: `unknown step kind: ${step.kind}` };
    }
  } catch (err) {
    if (err instanceof UiTargetMissingError) {
      return { status: 'target_missing', detail: err.message };
    }
    return {
      status: 'error',
      detail: err instanceof Error ? err.message : String(err),
    };
  } finally {
    void tMs(); // timing lands in the ack envelope via the runtime
  }
}

export type { UiStepKind };